import _ from "lodash";
import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";

import { useI18n } from "Hooks";
import ProfileCascade from "UIComponents/ProfileCascade";
import { formatDate } from "Utils";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";

import classes from "./UserAvatar.scss";

const getType = ({ isDeleted, isArchived }) =>
  isDeleted ? "DELETED" : isArchived ? "ARCHIVED" : "NORMAL";

const avatarStyles = ({ type }) => {
  const style = {
    NORMAL: {
      size: "small",
      shade: "light",
      randomizeColor: true,
      shape: "square",
    },
    ARCHIVED: {
      size: "small",
      shade: "light",
      color: "neutral",
      shape: "square",
    },
    DELETED: {
      size: "small",
      shade: "light",
      iconType: "individual",
      shape: "square",
    },
  };

  return style[type];
};

const WrapWithBrackets = ({ children }) => <>[{children}]</>;

export const UserName = ({
  user = { isDeleted: false, isArchived: true },
  fullName,
  createdAt = null,
}) => {
  const { t } = useI18n(["common"]);
  const type = getType(user);

  const suffix = {
    ARCHIVED: "common:archived",
    DELETED: "common:deleted_user",
  }[type];

  const userNameClassV2 = classNames(classes.userNameV2, "text-label-3");

  const dateClass = classNames("text-body-3", classes.date);

  return (
    <div className={classes.userNameContainerV2}>
      <div className={userNameClassV2}>{fullName}</div>
      {suffix ? (
        <div className="text-textSubtle">
          <WrapWithBrackets>{t(suffix)}</WrapWithBrackets>
        </div>
      ) : null}
      <div className={classes.separator}>•</div>
      {createdAt ? (
        <div className={dateClass}>
          {formatDate({
            date: createdAt,
            format: DATE_TIME_CONSTANTS.SENT_MESSAGE,
          })}
        </div>
      ) : null}
    </div>
  );
};

export const AVATAR_RENDER_MODES = {
  AVATAR: "avatar",
  SPACE: "space",
  NOTHING: "nothing",
};

const UserAvatar = ({ user, fullName, avatarRenderMode }) => {
  if (avatarRenderMode === AVATAR_RENDER_MODES.SPACE) {
    return <div className={classes.avatarSpace} />;
  }

  if (avatarRenderMode === AVATAR_RENDER_MODES.NOTHING) {
    return null;
  }

  const type = getType(user);

  const avatarItem = {
    id: user.id,
    value: fullName,
    imageUrl: user.profileImage,
  };

  return (
    <ProfileCascade
      items={[avatarItem]}
      showAvatar
      avatarStyles={avatarStyles({ type })}
    />
  );
};

UserAvatar.propTypes = {
  user: PropTypes.object.isRequired,
  fullName: PropTypes.string.isRequired,
  avatarRenderMode: PropTypes.oneOf(_.values(AVATAR_RENDER_MODES)),
};

UserAvatar.defaultProps = {
  avatarRenderMode: AVATAR_RENDER_MODES.AVATAR,
};

export default UserAvatar;
